import { CircularProgress } from "@mui/material";
import { lazy } from "react";
import { Switch } from "react-router";

import { RoutePathBuilder } from "@/common/builders/routePath";
import AuthenticatedGuard from "@/common/components/Auth/AuthenticatedGuard";
import { Loadable } from "@/common/components/Loadable";
import AppRoute from "@/common/components/Routing/AppRoute";
import { tenantService } from "@/common/services/tenant";

import { ROUTE_PATH } from "../../common/constants/routing";
import AuthLayout from "../Layouts/AuthLayout";
import BaseAppLayout from "../Layouts/BaseAppLayout";
import ErrorLayout from "../Layouts/ErrorLayout";

const Login = Loadable(
  lazy(() => import("../BaseApp/Auth/Login/Login")),
  <CircularProgress size={50} sx={{ m: "auto" }} />,
);
const Auth0CallbackHandler = Loadable(
  lazy(() => import("../BaseApp/Auth/Auth0CallbackHandlerPage")),
  <CircularProgress size={50} sx={{ m: "auto" }} />,
);
const CompleteInfo = Loadable(
  lazy(() => import("../BaseApp/Auth/CompleteInfo/CompleteInfo")),
  <CircularProgress size={50} sx={{ m: "auto" }} />,
);
const ForbiddenPage = Loadable(lazy(() => import("../BaseApp/Auth/ForbiddenPage")));
const Invite = Loadable(
  lazy(() => import("../BaseApp/Auth/Invite/Invite")),
  <CircularProgress size={50} sx={{ m: "auto" }} />,
);
const LoggedOut = Loadable(lazy(() => import("../BaseApp/Auth/LoggedOut/LoggedOut")));
const LogoutHandler = Loadable(
  lazy(() => import("../BaseApp/Auth/LogoutHandler/LogoutHandler")),
  <CircularProgress size={50} sx={{ m: "auto" }} />,
);
const LogoutBeforeRegisteringPage = Loadable(
  lazy(() => import("../BaseApp/Auth/Register/LogoutBeforeRegisteringPage")),
);
const TenantRegisterRoutes = Loadable(
  lazy(() => import("../BaseApp/Auth/Register/Tenant/TenantRegisterRoutes")),
  <CircularProgress size={50} sx={{ m: "auto" }} />,
);
const TeslaAuthCallbackHandler = Loadable(
  lazy(() => import("../BaseApp/Auth/Tesla/TeslaAuthCallbackHandler")),
  <CircularProgress size={50} sx={{ m: "auto" }} />,
);
const TenantAccountSuspendedErrorPage = Loadable(
  lazy(() =>
    import("../BaseApp/Error/TenantAccountSuspendedErrorPage").then((module) => ({
      default: module.TenantAccountSuspendedErrorPage,
    })),
  ),
  <CircularProgress size={50} sx={{ m: "auto" }} />,
);
const AccountSuspendedErrorPage = Loadable(
  lazy(() => import("../BaseApp/Error/AccountSuspendedErrorPage")),
  <CircularProgress size={50} sx={{ m: "auto" }} />,
);
const ApiConnectionErrorPage = Loadable(
  lazy(() => import("../BaseApp/Error/ApiConnectionErrorPage")),
  <CircularProgress size={50} sx={{ m: "auto" }} />,
);
const AppInitErrorPage = Loadable(lazy(() => import("../BaseApp/Error/AppInitErrorPage")));
const NoInternetErrorPage = Loadable(
  lazy(() => import("../BaseApp/Error/NoInternetErrorPage")),
  <CircularProgress size={50} sx={{ m: "auto" }} />,
);
const NotFoundErrorPage = Loadable(lazy(() => import("../BaseApp/Error/NotFoundErrorPage")));
const UnauthorizedCompanyAccessErrorPage = Loadable(
  lazy(() => import("../BaseApp/Error/UnauthorizedCompanyAccessErrorPage")),
  <CircularProgress size={50} sx={{ m: "auto" }} />,
);

const Profile = Loadable(lazy(() => import("../BaseApp/Profile/Profile")));
const RootRouteEntry = Loadable(lazy(() => import("../BaseApp/RootRouteEntry")));
const TenantSelectPage = Loadable(lazy(() => import("../BaseApp/TenantSelect/TenantSelectPage")));

export default function BaseAppContent() {
  // console.log("BaseAppContent", moment(performance.now()).format("HH:mm:ss.SSS"));

  return (
    <>
      {/* NB: don't use React.Fragment in Switch as it breaks routes. */}
      <Switch>
        {/* Auth0 callback URL handlers */}
        <AppRoute
          path={RoutePathBuilder.toDefinition(ROUTE_PATH.AUTH0_CALLBACK)}
          exact
          render={() => <Auth0CallbackHandler />}
        />

        {/* Tesla Auth callback URL handlers */}
        <AppRoute
          path={RoutePathBuilder.toDefinition(ROUTE_PATH.TESLA_AUTH_CALLBACK)}
          exact
          render={() => <TeslaAuthCallbackHandler />}
        />

        {/* Auth routes */}
        <AppRoute
          path={RoutePathBuilder.toDefinition(ROUTE_PATH.AUTH)}
          render={() => (
            <AuthLayout>
              <AppRoute
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.AUTH_SIGN_IN())}
                render={() => <Login />}
              />

              <AppRoute
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.AUTH_SIGN_UP)}
                render={() => {
                  // registration shouldn't be accessed via tenant URL
                  const tenantInfo = tenantService.resolveTenant(undefined, true);
                  if (
                    tenantInfo &&
                    tenantService.defaultTenantIdentifier !== tenantInfo?.identifier &&
                    !tenantService.isReservedTenantIdentifier(tenantInfo?.identifier)
                  ) {
                    tenantService.redirectToDefaultTenantUrl();
                    return;
                  } else {
                    return <TenantRegisterRoutes />;
                  }
                }}
              />

              <AppRoute
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.AUTH_SIGN_UP_MUST_SIGN_OUT_WARNING)}
                render={() => <LogoutBeforeRegisteringPage />}
              />

              <AppRoute
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.INVITE)}
                render={() => <Invite />}
              />

              <AppRoute
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.AUTH_COMPLETE_INFO())}
                render={() => <CompleteInfo />}
              />

              <AppRoute
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.AUTH_SIGN_OUT)}
                render={() => <LogoutHandler />}
              />
              <AppRoute
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.AUTH_SIGNED_OUT)}
                render={() => <LoggedOut />}
              />
              <AppRoute
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.AUTH_FORBIDDEN())}
                render={() => <ForbiddenPage />}
              />
            </AuthLayout>
          )}
        />

        {/* Errors */}
        <AppRoute
          path={RoutePathBuilder.toDefinition(ROUTE_PATH.ERROR)}
          render={() => (
            <ErrorLayout>
              <AppRoute
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.ERROR_APP_INIT)}
                render={() => <AppInitErrorPage />}
              />
              <AppRoute
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.ERROR_NO_API_CONNECTION())}
                render={() => <ApiConnectionErrorPage />}
              />
              <AppRoute
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.ERROR_NO_INTERNET())}
                render={() => <NoInternetErrorPage />}
              />
              <AppRoute
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.ERROR_NOT_FOUND)}
                render={() => <NotFoundErrorPage />}
              />
              <AppRoute
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.ERROR_UNAUTHORIZED_TENANT_ACCESS)}
                render={() => <UnauthorizedCompanyAccessErrorPage />}
              />
              <AppRoute
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.ERROR_TENANT_ACCOUNT_SUSPENDED())}
                render={() => <TenantAccountSuspendedErrorPage />}
              />
              <AppRoute
                exact
                path={RoutePathBuilder.toDefinition(ROUTE_PATH.ERROR_ACCOUNT_SUSPENDED())}
                render={() => <AccountSuspendedErrorPage />}
              />
            </ErrorLayout>
          )}
        />

        {/* Base routes */}

        {/* Authenticated */}
        <AppRoute
          exact
          path={RoutePathBuilder.toDefinition(ROUTE_PATH.SELECT_TENANT)}
          render={() => (
            <AuthenticatedGuard>
              <BaseAppLayout>
                <TenantSelectPage />
              </BaseAppLayout>
            </AuthenticatedGuard>
          )}
        />

        {/* Profile */}
        <AppRoute
          exact
          path={RoutePathBuilder.toDefinition(ROUTE_PATH.PROFILE)}
          render={() => (
            <BaseAppLayout>
              <Profile />
            </BaseAppLayout>
          )}
        />

        <AppRoute
          exact
          path='/'
          render={() => (
            <BaseAppLayout>
              <RootRouteEntry />
            </BaseAppLayout>
          )}
        />

        <AppRoute
          render={() => (
            <BaseAppLayout>
              <NotFoundErrorPage />
            </BaseAppLayout>
          )}
        />
      </Switch>
    </>
  );
}
